
import { Options, Vue } from "vue-class-component";
import Breadcrumb from "@/app/ui/components/breadcrumb/index.vue";
import Search from "@/app/ui/components/search/index.vue";
import Icons from "@/app/ui/components/icons/index.vue";
import TransferPickupModal from "./component/modal.vue";
import DateRangePicker from "@/app/ui/components/date-range-picker/index.vue";
import PaginationV3 from "@/app/ui/components/pagination-v3/index.vue";
import EmptyList from "@/app/ui/components/empty-list/index.vue";
import { PickupDashboardController } from "@/app/ui/controllers/PickupDashboardController";
import {
  TransferPickupListRequest,
  SenderData
} from "@/domain/entities/PickupDashboard";
import { Pagination } from "@/domain/entities/Pagination";
import { OptionsClass } from "@/domain/entities/MainApp";
import { debounce } from "lodash";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { ExportApiRequest } from "@/data/payload/api/PickupDashboardApiRequest";
import {
  formatDateNumber,
  parsingErrorResponse,
  formatByCountry
} from "@/app/infrastructures/misc/Utils";
import { ReportController } from "@/app/ui/controllers/ReportController";
import TableLazyLoad from "@/app/ui/components/lazy-load/table-lazy-load.vue";
import DownloadWithDateRange from "../../components/download-with-date-range/index.vue";
import { DownloadReportRequest } from "@/domain/entities/Kulioner";
import formatPhoneNumber from "@/app/infrastructures/misc/common-library/v2/FormatPhoneNumber";

interface SelectedSellerData {
  id: string;
  sellerName: string;
  sellerPhone: string;
  packageCount: number;
  koli: number;
  weight: number;
  taskGroupId?: number;
  index: number;
}

@Options({
  components: {
    Breadcrumb,
    Search,
    Icons,
    TransferPickupModal,
    DateRangePicker,
    PaginationV3,
    EmptyList,
    TableLazyLoad,
    DownloadWithDateRange
  }
})
class PickupDashboardMonitoringCourierTransferPickup extends Vue {
  formatPhoneNumber = formatPhoneNumber;

  waNumber(phone: string) {
    return String(phone || "").replace(/\D/g, "");
  }

  courierId = "";
  courierName = "";
  courierPhone = "";
  dateRange: { startDate: string; endDate: string } | null = null;
  dateRangeValue: { start: Date | null; end: Date | null } = {
    start: null,
    end: null
  };
  shipmentType = "";
  selectedShipmentTypes: OptionsClass[] = [];
  searchKeyword = "";
  isSearchApplied = false;
  showTransferModal = false;
  selectedSellers: SelectedSellerData[] = [];
  currentPage = 1;
  countdownIntervals: { [key: string]: NodeJS.Timeout } = {};
  currentSort = "total_shipment";
  currentSortOrder = "DESC";

  shipmentTypes = [
    new OptionsClass({
      name: "AGGREGATOR",
      value: "AGGREGATOR"
    }),
    new OptionsClass({
      name: "MARKETPLACE",
      value: "MARKETPLACE"
    }),
    new OptionsClass({
      name: "CUSTOMER APP",
      value: "CUSTOMER_APP"
    }),
    new OptionsClass({
      name: "CORPORATE",
      value: "CORPORATE"
    })
  ];

  get columns() {
    return [
      { key: "checkbox", label: "", width: "w-8" },
      { key: "no", label: this.$t("pickupDashboard.column.no"), width: "w-16" },
      {
        key: "sender_name",
        label: this.$t("pickupDashboard.column.senderName"),
        sortable: true
      },
      {
        key: "shipment_type",
        label: this.$t("pickupDashboard.column.shipmentType")
      },
      {
        key: "total_shipment",
        label: this.$t("pickupDashboard.column.totalShipment"),
        sortable: true
      },
      {
        key: "expired_at",
        label: this.$t("pickupDashboard.column.remainingTime"),
        sortable: true
      },
      { key: "address", label: this.$t("pickupDashboard.column.address") },
      {
        key: "transfer_action",
        label: this.$t("pickupDashboard.column.transferPickUp"),
        width: "w-24"
      }
    ];
  }

  get headerTitle() {
    const parts = [
      this.courierName,
      this.courierId,
      this.formatPhoneNumber(this.courierPhone)
    ].filter(Boolean);
    return `${this.$t("pickupDashboard.transferPickUp")}: ${parts.join(" • ")}`;
  }

  mounted() {
    this.courierId = String(this.$route.params.id || "");
    this.courierName = String(this.$route.query.courier_name || "");
    this.courierPhone = String(this.$route.query.courier_phone || "");

    // Initialize with null dateRange (all time)
    this.dateRange = null;
    this.dateRangeValue = { start: null, end: null };

    this.loadSenderList();
  }

  async loadSenderList() {
    try {
      // Convert selected shipment types to comma-separated string
      const shipmentTypeValue =
        this.selectedShipmentTypes.length > 0
          ? this.selectedShipmentTypes.map((type) => type.value).join(",")
          : "";

      const params = new TransferPickupListRequest({
        // eslint-disable-next-line @typescript-eslint/camelcase
        courier_id: parseInt(this.courierId),
        // eslint-disable-next-line @typescript-eslint/camelcase
        start_date: this.dateRange?.startDate || "",
        // eslint-disable-next-line @typescript-eslint/camelcase
        end_date: this.dateRange?.endDate || "",
        status: "CRROTW",
        q: this.searchKeyword,
        // eslint-disable-next-line @typescript-eslint/camelcase
        shipment_type: shipmentTypeValue,
        page: this.currentPage,
        limit: 20,
        // eslint-disable-next-line @typescript-eslint/camelcase
        sort_by: this.currentSortOrder.toUpperCase(),
        // eslint-disable-next-line @typescript-eslint/camelcase
        order_by: this.currentSortOrder ? this.currentSort : ""
      });

      const result = await PickupDashboardController.getSenderList(params);
      this.startCountdownTimers();
    } catch (error) {
      console.error("Error loading sender list:", error);
    }
  }

  startCountdownTimers() {
    // Clear existing intervals
    Object.values(this.countdownIntervals).forEach((interval) =>
      clearInterval(interval)
    );
    this.countdownIntervals = {};

    // Start new intervals for each sender
    this.senderList.data.forEach((sender) => {
      if (sender.expiredAt) {
        this.countdownIntervals[sender.id] = setInterval(() => {
          this.updateCountdown(sender.id);
        }, 1000);
      }
    });
  }

  updateCountdown(senderId: string) {
    const sender = this.senderList.data.find((s) => s.id === senderId);
    if (!sender || !sender.expiredAt) return;

    // Force reactivity update
    this.$forceUpdate();
  }

  formatCountdown(expiredAt: string): { display: string; isUrgent: boolean } {
    if (!expiredAt) return { display: "00:00:00", isUrgent: false };

    const now = new Date().getTime();
    const expiredTime = new Date(expiredAt).getTime();
    const remainingTime = Math.max(0, expiredTime - now);

    const hours = Math.floor(remainingTime / (1000 * 60 * 60));
    const minutes = Math.floor(
      (remainingTime % (1000 * 60 * 60)) / (1000 * 60)
    );
    const seconds = Math.floor((remainingTime % (1000 * 60)) / 1000);

    const display = `${hours
      .toString()
      .padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
    const isUrgent = remainingTime <= 1800000; // 30 minutes in milliseconds

    return { display, isUrgent };
  }

  onSearchInput(value: string) {
    if (!!value && value.length < 3) return;
    this.searchKeyword = value;
    this.isSearchApplied = !!this.searchKeyword;
    this.currentPage = 1;
    this.loadSenderList();
  }

  onClearSearch() {
    this.searchKeyword = "";
    this.isSearchApplied = false;
    this.currentPage = 1;
    this.loadSenderList();
  }

  onDateRangeApply(range: { start: Date | null; end: Date | null }) {
    if (!range.start || !range.end) {
      this.dateRange = null;
    } else {
      // Format dates to YYYY-MM-DD
      const formatDate = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      };

      this.dateRange = {
        startDate: formatDate(range.start),
        endDate: formatDate(range.end)
      };
    }

    this.currentPage = 1;
    this.loadSenderList();
  }

  onDateRangeReset() {
    this.dateRange = null;
    this.dateRangeValue = { start: null, end: null };
    this.currentPage = 1;
    this.loadSenderList();
  }

  onShipmentTypeChange() {
    this.currentPage = 1;
    this.loadSenderList();
  }

  goBack() {
    this.$router.push("/pickup-dashboard/monitoring/courier");
  }

  handleDownloadDataPickup = debounce(async (date: DownloadReportRequest) => {
    try {
      MainAppController.showLoading();

      await PickupDashboardController.export(
        new ExportApiRequest({
          ...date,
          q: this.searchKeyword,
          courierId: Number(this.courierId)
        })
      );

      await ReportController.onGetListDownloadReport();
      ReportController.setOpenDownloadRequest(true);
    } catch (err) {
      MainAppController.showErrorMessage(
        parsingErrorResponse(
          err,
          "Gagal Download Data !",
          this.handleDownloadDataPickup
        )
      );
    } finally {
      MainAppController.closeLoading();
    }
  }, 400);

  openTransferModal() {
    this.showTransferModal = true;
  }

  handleRemoveSeller(sellerIndex: number) {
    this.selectedSellers = this.selectedSellers.filter(
      (seller) => seller.index !== sellerIndex
    );

    if (this.selectedSellers.length === 0) {
      this.showTransferModal = false;
    }
  }

  handleTransferSuccess() {
    // Reset selected sellers and close modal
    this.selectedSellers = [];
    this.showTransferModal = false;

    // Refresh sender list
    this.loadSenderList();
  }

  toggleSenderSelection(sender: SenderData, index: number) {
    const uniqueKey = `${sender.id}-${index}`;
    const selectedIndex = this.selectedSellers.findIndex(
      (s) => `${s.id}-${s.index}` === uniqueKey
    );
    if (selectedIndex > -1) {
      this.selectedSellers.splice(selectedIndex, 1);
    } else {
      this.selectedSellers.push({
        id: sender.id,
        sellerName: sender.senderName,
        sellerPhone: sender.senderPhone,
        packageCount: sender.totalShipment,
        koli: sender.totalQuantity,
        weight: sender.totalWeight,
        taskGroupId: sender.taskGroupId,
        index: index
      });
    }
  }

  isSenderSelected(sender: SenderData, index: number): boolean {
    const uniqueKey = `${sender.id}-${index}`;
    return this.selectedSellers.some((s) => `${s.id}-${s.index}` === uniqueKey);
  }

  openSingleTransferModal(sender: SenderData) {
    const senderIndex = this.senderList.data.findIndex(
      (s) => s.id === sender.id
    );
    this.selectedSellers = [
      {
        id: sender.id,
        sellerName: sender.senderName,
        sellerPhone: sender.senderPhone,
        packageCount: sender.totalShipment,
        koli: sender.totalQuantity,
        weight: sender.totalWeight,
        taskGroupId: sender.taskGroupId,
        index: senderIndex
      }
    ];
    this.showTransferModal = true;
  }

  onPageChange(page: number) {
    this.currentPage = page;
    this.loadSenderList();
  }

  get senderList() {
    return PickupDashboardController.senderList;
  }

  get isLoadingSenderList() {
    return PickupDashboardController.isLoadingSenderList;
  }

  get selectedCount() {
    return this.selectedSellers.length;
  }

  get hasSearchOrFilter() {
    return (
      !!this.searchKeyword ||
      !!this.dateRange ||
      this.selectedShipmentTypes.length > 0
    );
  }

  get paginationObject() {
    return new Pagination(
      this.currentPage,
      this.senderList.meta.limit,
      this.senderList.meta.totalRecords
    );
  }

  toggleAllSenders() {
    if (this.selectedCount === this.senderList.data.length) {
      // Deselect all
      this.selectedSellers = [];
    } else {
      // Select all
      this.selectedSellers = this.senderList.data.map((sender, index) => ({
        id: sender.id,
        sellerName: sender.senderName,
        sellerPhone: sender.senderPhone,
        packageCount: sender.totalShipment,
        koli: sender.totalQuantity,
        weight: sender.totalWeight,
        taskGroupId: sender.taskGroupId,
        index: index
      }));
    }
  }

  fmtQty(val: number | string) {
    return formatByCountry(Number(val || 0), { type: "quantity" });
  }

  getShipmentTypeLabel(shipmentType: string): string {
    const typeMap: { [key: string]: string } = {
      AGGREGATOR: "Aggregator",
      MARKETPLACE: "Marketplace",
      CUSTOMER_APP: "Customer App",
      CORPORATE: "Corporate"
    };
    return typeMap[shipmentType] || shipmentType;
  }

  getGoogleMapsUrl(sender: SenderData): string {
    if (sender.lat !== 0 && sender.lon !== 0) {
      return `https://www.google.com/maps?q=${sender.lat},${sender.lon}`;
    } else {
      return `https://maps.google.com?q=${encodeURIComponent(
        sender.senderAddress
      )}`;
    }
  }

  sortBy(field: string) {
    if (this.currentSort === field) {
      // 3-state cycle: Default → ASC → DESC → Default
      if (this.currentSortOrder === "") {
        this.currentSortOrder = "ASC";
      } else if (this.currentSortOrder === "ASC") {
        this.currentSortOrder = "DESC";
      } else if (this.currentSortOrder === "DESC") {
        this.currentSortOrder = "";
        this.currentSort = ""; // Reset to default
      }
    } else {
      // New field, start with ASC
      this.currentSort = field;
      this.currentSortOrder = "ASC";
    }

    this.currentPage = 1;
    this.loadSenderList();
  }

  beforeUnmount() {
    // Clear all countdown intervals
    Object.values(this.countdownIntervals).forEach((interval) =>
      clearInterval(interval)
    );
  }
}
export default PickupDashboardMonitoringCourierTransferPickup;
